import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TablePagination, Chip, IconButton, Tooltip, TextField, InputAdornment, Grid,
  Card, CardContent, FormControl, InputLabel, Select, MenuItem, Stack, CircularProgress,
  Snackbar, Alert, Collapse
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, VisibilityOutlined as ViewIcon, Search as SearchIcon,
  SyncProblem as SyncProblemIcon, CheckCircleOutline as SyncedIcon, Assignment as CasesIcon,
  EventNote as AppointmentsIcon, FilterList as FilterIcon, ExpandLess, ExpandMore
} from '@mui/icons-material';

import CaptureModal from '../../components/modals/CaptureModal';
import { useDashboardService } from '../../services/dashboardService';
import { useReferralService } from '../../services/referralService';
import { useCatalogService } from '../../services/catalogService';

// ✅ ESTADOS FINALES: Eliminado ENVIADO
const statusColorMap: any = {
  SINCRONIZADO: 'success',
  SIN_CITAS: 'warning',
  ERROR: 'error',
  PENDIENTE: 'warning'
};

// ✅ ESTADOS FINALES: Eliminado ENVIADO
const statusLabelMap: any = {
  SINCRONIZADO: 'Sincronizado',
  SIN_CITAS: 'Sin citas',
  ERROR: 'Error',
  PENDIENTE: 'Pendiente'
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [referrals, setReferrals] = useState<any[]>([]);
  const [totalReferralsCount, setTotalReferralsCount] = useState<number>(0);

  // Filtros y paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [productFilter, setProductFilter] = useState('ALL');
  const [originFilter, setOriginFilter] = useState('ALL');
  const [tableLoading, setTableLoading] = useState(false);

  // Modal de captura
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);
  const [isViewOnly, setIsViewOnly] = useState(false);

  // Estado para ocultar/mostrar estadísticas y filtros juntos
  const [showStatsAndFilters, setShowStatsAndFilters] = useState(true);

  // Totales
  const [totals, setTotals] = useState({
    totalReferrals: 0,
    totalClaims: 0,
    pendingSync: 0,
    syncErrors: 0
  });
  const [totalsLoading, setTotalsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<any>({ open: false, message: '', severity: 'info' });

  // Servicios
  const { getTotalReferrals, getTotalClaims, getPendingSync, getSyncErrors } = useDashboardService();
  const { getReferrals } = useReferralService();
  const { getAllCatalogs } = useCatalogService();

  // Productos del catálogo
  const [products, setProducts] = useState<any[]>([]);
  const [productsLoading, setProductsLoading] = useState(true);

  // Log cuando cambia el estado de productos
  useEffect(() => {
    console.log('🔄 Estado de productos actualizado:', {
      count: products.length,
      products: products,
      loading: productsLoading
    });
  }, [products, productsLoading]);

  // Cargar referrals
  const fetchReferrals = async () => {
    try {
      setTableLoading(true);

      const filters: any = {};

      if (statusFilter && statusFilter !== 'ALL') {
        filters.status = statusFilter;
      } else if (searchTerm.trim()) {
        filters.search = searchTerm.trim();
      }

      if (productFilter && productFilter !== 'ALL') {
        filters.product = productFilter;
      }

      // ✅ CORREGIDO: Siempre enviar el filtro origin (incluso si es 'ALL')
      filters.origin = originFilter;
      // Cola ComPsych: MANUAL (siempre) + PHONE_CALL marcados para Sync
      filters.forSyncQueue = true;

      const data = await getReferrals(page, rowsPerPage, filters);
      setReferrals(data.content || []);
      setTotalReferralsCount(data.totalElements || 0);
    } catch (err: any) {
      console.error('❌ Error al cargar referrals:', err);
      setSnackbar({ open: true, message: 'Error al cargar casos', severity: 'error' });
    } finally {
      setTableLoading(false);
    }
  };

  // Cargar productos del catálogo
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setProductsLoading(true);
        console.log('🔄 Cargando productos del catálogo...');
        const catalogsData = await getAllCatalogs();
        console.log('📊 Datos completos del catálogo:', catalogsData);
        console.log('📊 Claves del catálogo:', Object.keys(catalogsData || {}));
        
        // Intentar obtener productos de diferentes formas posibles
        let productsList = catalogsData?.PRODUCT || catalogsData?.product || [];
        
        // Si es un objeto, convertir a array
        if (!Array.isArray(productsList) && typeof productsList === 'object') {
          productsList = Object.values(productsList);
        }
        
        // Filtrar productos válidos (que tengan code)
        productsList = productsList.filter((p: any) => p && p.code);
        
        console.log('📦 Productos encontrados (total):', productsList.length);
        console.log('📦 Lista completa de productos:', productsList);
        console.log('📦 Estructura de cada producto:', productsList.map((p: any, i: number) => ({
          index: i,
          code: p.code,
          description: p.description,
          full: p
        })));
        
        setProducts(productsList);
      } catch (err: any) {
        console.error('❌ Error al cargar productos:', err);
        console.error('❌ Error details:', err.message, err.stack);
        setSnackbar({ open: true, message: 'Error al cargar productos', severity: 'error' });
      } finally {
        setProductsLoading(false);
      }
    };
    loadProducts();
    // eslint-disable-next-line
  }, []);

  // Cargar totales una vez
  useEffect(() => {
    const fetchTotals = async () => {
      try {
        setTotalsLoading(true);
        const [
          totalReferrals,
          totalClaims,
          pendingSync,
          syncErrors
        ] = await Promise.all([
          getTotalReferrals(),
          getTotalClaims(),
          getPendingSync(),
          getSyncErrors()
        ]);
        setTotals({
          totalReferrals: totalReferrals || 0,
          totalClaims: totalClaims || 0,
          pendingSync: pendingSync || 0,
          syncErrors: syncErrors || 0
        });
      } catch (err: any) {
        setSnackbar({ open: true, message: 'Error al cargar totales', severity: 'error' });
      } finally {
        setTotalsLoading(false);
      }
    };
    fetchTotals();
  }, []);

  // Resetear página cuando cambian los filtros (pero no cuando cambia page manualmente)
  useEffect(() => {
    setPage(0);
    // eslint-disable-next-line
  }, [searchTerm, statusFilter, productFilter, originFilter]);

  // Cargar referrals al cambiar página/filtros
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchReferrals();
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line
  }, [page, rowsPerPage, searchTerm, statusFilter, productFilter, originFilter]);

  const displayReferrals = useMemo(() => {
    // ✅ FUNCIÓN HELPER: Determinar qué productos pueden tener claims
    const canHaveClaims = (product: string): boolean => {
      return ['EAP', 'HWB - Tobacco & Nicotine Cessation', 'HWB - Weight Management', 'HWB - Well-Being Coaching'].includes(product);
    };

    let mapped = referrals.map((ref: any) => ({
      id: ref.id,
      externalReferralId: ref.externalReferralId ?? 'NO_RECIBIDO',
      clientId: ref.clientId ?? 'NO_RECIBIDO',
      // ✅ CLAIM ID CORREGIDO
      externalClaimId: !canHaveClaims(ref.product)
        ? 'N/A'
        : ref.claims && ref.claims.length > 0
          ? ref.claims[0].externalClaimId ?? 'PENDIENTE'
          : 'SIN_CLAIM',
      clientName: `${ref.firstName ?? ''} ${ref.lastName ?? ''}`.trim(),
      accountName: ref.accountPathName,
      date: ref.callDate ? new Date(ref.callDate + 'T12:00:00').toLocaleDateString('es-MX') : '',

      product: ref.product,
      // ✅ ESTADO CORREGIDO: Mapear SIN_CITAS a SINCRONIZADO para productos sin claims
      status: !canHaveClaims(ref.product) && ref.status === 'SIN_CITAS'
        ? 'SINCRONIZADO'
        : ref.status,
      origin: ref.origin || 'N/A', // ✅ NUEVO: Campo origin
      isSynced: ref.isSynced,
      syncAttempts: ref.syncAttempts,
      syncErrorMessage: ref.syncErrorMessage
    }));

    return mapped;
  }, [referrals]);

  // Handlers
  const handleNewCapture = () => {
    setSelectedRecord(null);
    setIsViewOnly(false);
    setModalOpen(true);
  };

  const handleViewRecord = (record: any) => {
    const original = referrals.find((r) => r.id === record.id);
    setSelectedRecord(original || null);
    setIsViewOnly(true);
    setModalOpen(true);
  };

  const handleEditRecord = (record: any) => {
    const original = referrals.find((r) => r.id === record.id);
    setSelectedRecord(original || null);
    setIsViewOnly(false);
    setModalOpen(true);
  };

  const handleCloseModal = (refreshData = false) => {
    setModalOpen(false);
    setSelectedRecord(null);
    setIsViewOnly(false);
    if (refreshData) {
      fetchReferrals();
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // Calcular altura máxima de la tabla dinámicamente
  const tableMaxHeight = useMemo(() => {
    // Valor base cuando estadísticas y filtros están visibles (valor original)
    const baseSpace = 500;
    
    // Reducir el espacio cuando ambas secciones están colapsadas
    // Cuando colapsadas: ahorra ~350px (150px estadísticas + 200px filtros)
    const savedSpace = showStatsAndFilters ? 0 : 350;
    
    const totalSpace = baseSpace - savedSpace;
    
    return `calc(100vh - ${totalSpace}px)`;
  }, [showStatsAndFilters]);

  // --- UI ---
  return (
    <Box sx={{ pt: 1, px: 3, pb: 3, overflowX: 'auto', width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Sincronización ComPsych
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Capturas manuales y casos de llamada telefónica marcados para Sync
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewCapture}
          size="large"
        >
          Nueva Captura
        </Button>
      </Box>

      {/* Estadísticas y Filtros */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Tooltip title={showStatsAndFilters ? 'Ocultar estadísticas y filtros' : 'Mostrar estadísticas y filtros'}>
            <IconButton
              size="small"
              onClick={() => setShowStatsAndFilters(!showStatsAndFilters)}
              color="primary"
            >
              {showStatsAndFilters ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Tooltip>
        </Box>
        <Collapse in={showStatsAndFilters}>
          <Box>
            {/* Estadísticas */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold', mb: 2 }}>
                Estadísticas
              </Typography>
              <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <CasesIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {totalsLoading ? <CircularProgress size={24} /> : totals.totalReferrals}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Casos
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <AppointmentsIcon sx={{ fontSize: 40, color: 'info.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                        {totalsLoading ? <CircularProgress size={24} /> : totals.totalClaims}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total de Citas
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <SyncProblemIcon sx={{ fontSize: 40, color: 'warning.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                        {totalsLoading ? <CircularProgress size={24} /> : totals.pendingSync}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Pendientes Sync
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                    <SyncProblemIcon sx={{ fontSize: 40, color: 'error.main', mr: 2 }} />
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                        {totalsLoading ? <CircularProgress size={24} /> : totals.syncErrors}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Errores de Sync
                      </Typography>
                    </Box>
                  </CardContent>
              </Card>
              </Grid>
            </Grid>
            </Box>

            {/* Filtros */}
            <Paper sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FilterIcon color="primary" />
                  <Typography variant="h6" color="primary.main" sx={{ fontWeight: 'bold' }}>
                    Filtros de Búsqueda
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 2, fontStyle: 'italic' }}>
                    (El filtro de Estado tiene prioridad sobre la Búsqueda)
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    placeholder="Buscar por cliente, cuenta o referencia..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  {/* ✅ FILTRO SIN ENVIADO */}
                  <FormControl fullWidth>
                    <InputLabel>Estado</InputLabel>
                    <Select
                      value={statusFilter}
                      onChange={e => setStatusFilter(e.target.value)}
                      label="Estado"
                    >
                      <MenuItem value="ALL">Todos</MenuItem>
                      <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                      <MenuItem value="SIN_CITAS">Sin citas</MenuItem>
                      <MenuItem value="SINCRONIZADO">Sincronizado</MenuItem>
                      <MenuItem value="ERROR">Error</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Producto</InputLabel>
                    <Select
                      value={productFilter}
                      onChange={e => setProductFilter(e.target.value)}
                      label="Producto"
                      disabled={productsLoading}
                    >
                      <MenuItem value="ALL">Todos</MenuItem>
                      {products.length > 0 ? (
                        products.map((product: any, index: number) => {
                          console.log(`📋 Renderizando producto ${index}:`, product);
                          return (
                            <MenuItem key={product.code || index} value={product.code}>
                              {product.description || product.code}
                            </MenuItem>
                          );
                        })
                      ) : (
                        !productsLoading && (
                          <MenuItem value="" disabled>
                            No hay productos disponibles (cargados: {products.length})
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel>Origen</InputLabel>
                    <Select
                      value={originFilter}
                      onChange={e => setOriginFilter(e.target.value)}
                      label="Origen"
                    >
                      <MenuItem value="ALL">Todos</MenuItem>
                      <MenuItem value="MANUAL">Manual</MenuItem>
                      <MenuItem value="PHONE_CALL">Llamada Telefónica</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSearchTerm('');
                      setStatusFilter('ALL');
                      setProductFilter('ALL');
                      setOriginFilter('ALL');
                      setPage(0);
                    }}
                    sx={{ height: '56px' }}
                  >
                    Limpiar Filtros
                  </Button>
                </Grid>
              </Grid>

              {(searchTerm || statusFilter !== 'ALL' || productFilter !== 'ALL' || originFilter !== 'ALL') && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Filtros activos:
                  </Typography>
                  {searchTerm && (
                    <Chip
                      label={`Búsqueda: "${searchTerm}"`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={() => setSearchTerm('')}
                    />
                  )}
                  {statusFilter !== 'ALL' && (
                    <Chip
                      label={`Estado: ${statusLabelMap[statusFilter] || statusFilter}`}
                      size="small"
                      color="info"
                      variant="outlined"
                      onDelete={() => setStatusFilter('ALL')}
                    />
                  )}
                  {productFilter !== 'ALL' && (
                    <Chip
                      label={`Producto: ${productFilter}`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      onDelete={() => setProductFilter('ALL')}
                    />
                  )}
                  {originFilter !== 'ALL' && (
                    <Chip
                      label={`Origen: ${originFilter === 'MANUAL' ? 'Manual' : originFilter === 'PHONE_CALL' ? 'Llamada Telefónica' : originFilter}`}
                      size="small"
                      color="default"
                      variant="outlined"
                      onDelete={() => setOriginFilter('ALL')}
                    />
                  )}
                </Box>
              )}
              </Stack>
            </Paper>
          </Box>
        </Collapse>
      </Box>

      {/* Tabla Principal */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: tableMaxHeight }}>
          <Table stickyHeader aria-label="tabla del dashboard">
            <TableHead>
              <TableRow>
                <TableCell align="center">Caso</TableCell>
                <TableCell align="center">Client ID</TableCell>
                <TableCell align="center">Claim ID</TableCell>
                <TableCell align="center">Cliente</TableCell>
                <TableCell align="center">Cuenta</TableCell>
                <TableCell align="center">Fecha</TableCell>
                <TableCell align="center">Producto</TableCell>
                <TableCell align="center">Origen</TableCell>
                <TableCell align="center">Estado</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableLoading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    <CircularProgress size={24} /> Cargando registros...
                  </TableCell>
                </TableRow>
              ) : displayReferrals.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center">
                    No se encontraron registros.
                  </TableCell>
                </TableRow>
              ) : (
                displayReferrals.map((ref, idx) => {
                  const statusProps = {
                    color: statusColorMap[ref.status] || 'warning',
                    label: statusLabelMap[ref.status] || ref.status
                  };
                  return (
                    <TableRow hover key={ref.id || idx}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {ref.externalReferralId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: ref.clientId === 'PENDIENTE' ? 'warning.main' : 'info.main',
                            fontWeight: ref.clientId === 'PENDIENTE' ? 'normal' : 'bold'
                          }}
                        >
                          {ref.clientId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: ref.externalClaimId === 'N/A' ? 'text.secondary' :
                              ref.externalClaimId === 'SIN_CLAIM' ? 'error.main' :
                                ref.externalClaimId === 'PENDIENTE' ? 'warning.main' :
                                  'success.main',
                            fontWeight: ref.externalClaimId === 'N/A' ||
                              ref.externalClaimId === 'SIN_CLAIM' ||
                              ref.externalClaimId === 'PENDIENTE' ? 'normal' : 'bold',
                            fontStyle: ref.externalClaimId === 'SIN_CLAIM' ? 'italic' : 'normal'
                          }}
                        >
                          {ref.externalClaimId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ref.clientName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{ref.accountName}</Typography>
                      </TableCell>
                      <TableCell>{ref.date}</TableCell>
                      <TableCell>
                        {ref.product && (
                          <Chip label={ref.product} size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={ref.origin === 'MANUAL' ? 'Manual' : ref.origin === 'PHONE_CALL' ? 'Llamada Telefónica' : ref.origin}
                          size="small"
                          color={ref.origin === 'MANUAL' ? 'primary' : ref.origin === 'PHONE_CALL' ? 'secondary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          {/* ✅ ACTUALIZADO: Chip muestra correctamente SIN_CITAS */}
                          <Chip
                            label={statusProps.label}
                            size="small"
                            color={statusProps.color}
                            // ✅ Icono especial para SIN_CITAS (usando AppointmentsIcon que es EventNote)
                            icon={ref.status === 'SIN_CITAS' ? <AppointmentsIcon fontSize="small" /> : undefined}
                          />
                          {ref.isSynced ? (
                            <Tooltip title="Sincronizado con ComPsych">
                              <SyncedIcon color="success" fontSize="small" />
                            </Tooltip>
                          ) : ref.syncAttempts > 0 ? (
                            <Tooltip title={`Error de sincronización: ${ref.syncErrorMessage || 'Error desconocido'}`}>
                              <SyncProblemIcon color="error" fontSize="small" />
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Ver detalles del registro">
                            <IconButton
                              color="info"
                              onClick={() => handleViewRecord(ref)}
                              size="small"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>

                          {/* ✅ TODOS LOS ESTADOS SON EDITABLES (no hay ENVIADO) */}
                          <Tooltip title={
                            ref.status === 'SIN_CITAS'
                              ? "Editar y agregar citas al registro"
                              : ref.status === 'SINCRONIZADO'
                                ? "Editar y agregar más citas al registro"
                                : ref.status === 'ERROR'
                                  ? "Editar y corregir errores del registro"
                                  : "Editar y enviar a ComPsych"
                          }>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditRecord(ref)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalReferralsCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por página"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />
      </Paper>

      {/* Modal de Captura */}
      {modalOpen && (
        <CaptureModal
          open={modalOpen}
          onClose={handleCloseModal}
          editRecord={selectedRecord}
          viewOnly={isViewOnly}
        />
      )}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;